import OSS from 'ali-oss'
import COS from 'cos-js-sdk-v5'
import { getOssStsToken, type OssStsToken } from './api'

type CloudClient = OSS | COS

let ossClient: CloudClient | null = null
let stsToken: OssStsToken | null = null
let tokenExpirationTime: number = 0
let currentProvider: 'aliyun' | 'tencent' = 'aliyun'

// 初始化云存储客户端
async function initCloudClient(): Promise<CloudClient> {
  // 检查当前凭证是否有效（提前5分钟刷新）
  const now = Date.now()
  if (ossClient && stsToken && tokenExpirationTime > now + 5 * 60 * 1000) {
    return ossClient
  }

  // 获取新的 STS 凭证
  const newToken = await getOssStsToken()
  if (!newToken) {
    throw new Error('获取云存储 STS 凭证失败')
  }

  stsToken = newToken
  currentProvider = newToken.provider || 'aliyun'
  
  // 计算过期时间
  const expiration = typeof newToken.credentials.expiration === 'string'
    ? new Date(newToken.credentials.expiration).getTime()
    : newToken.credentials.expiration * 1000
  tokenExpirationTime = expiration

  if (currentProvider === 'aliyun') {
    // 创建阿里云 OSS 客户端
    ossClient = new OSS({
      accessKeyId: newToken.credentials.accessKeyId,
      accessKeySecret: newToken.credentials.accessKeySecret,
      stsToken: newToken.credentials.securityToken,
      bucket: newToken.bucket,
      region: newToken.region,
      endpoint: newToken.endpoint,
      secure: true // 使用 HTTPS
    })
  } else if (currentProvider === 'tencent') {
    // 创建腾讯云 COS 客户端
    ossClient = new COS({
      SecretId: newToken.credentials.accessKeyId,
      SecretKey: newToken.credentials.accessKeySecret,
      SecurityToken: newToken.credentials.securityToken
    } as any)
  } else {
    throw new Error(`不支持的云服务商: ${currentProvider}`)
  }

  return ossClient
}

// 上传文件到云存储
export async function uploadFileToOSS(
  file: File | Blob,
  objectKey: string
): Promise<{ url: string; fileId: string }> {
  try {
    const client = await initCloudClient()
    
    if (currentProvider === 'aliyun') {
      // 阿里云 OSS 上传
      const ossClient = client as OSS
      const result = await ossClient.put(objectKey, file)
      return {
        url: result.url,
        fileId: objectKey
      }
    } else if (currentProvider === 'tencent') {
      // 腾讯云 COS 上传
      const cosClient = client as COS
      if (!stsToken) {
        throw new Error('STS 凭证未初始化')
      }
      
      return new Promise((resolve, reject) => {
        cosClient.putObject({
          Bucket: stsToken!.bucket,
          Region: stsToken!.region,
          Key: objectKey,
          Body: file
        }, (err: any, _data: any) => {
          if (err) {
            console.error('上传文件到腾讯云COS失败:', err)
            reject(err)
          } else {
            const url = `https://${stsToken!.bucket}.cos.${stsToken!.region}.myqcloud.com/${objectKey}`
            resolve({
              url: url,
              fileId: objectKey
            })
          }
        })
      })
    } else {
      throw new Error(`不支持的云服务商: ${currentProvider}`)
    }
  } catch (error) {
    console.error('上传文件到云存储失败:', error)
    throw error
  }
}

// 生成云存储文件访问 URL
export function getOssFileUrl(
  objectKey: string,
  bucket: string,
  region: string,
  endpoint?: string,
  provider: 'aliyun' | 'tencent' = 'aliyun'
): string {
  // 根据云服务商生成标准格式 URL
  if (provider === 'tencent') {
    // 腾讯云 COS URL 格式: https://{bucket}.cos.{region}.myqcloud.com/{objectKey}
    // 注意：腾讯云的 endpoint 通常是通用格式（如 https://cos.ap-chengdu.myqcloud.com），
    // 不包含 bucket，所以需要使用标准的 bucket URL 格式
    return `https://${bucket}.cos.${region}.myqcloud.com/${objectKey}`
  } else {
    // 阿里云 OSS
    if (endpoint) {
      // 如果提供了自定义 endpoint（如自定义域名），直接使用
      return `${endpoint}/${objectKey}`
    }
    // 阿里云 OSS URL 格式: https://{bucket}.oss-{region}.aliyuncs.com/{objectKey}
    return `https://${bucket}.oss-${region}.aliyuncs.com/${objectKey}`
  }
}

// 生成文件对象键（objectKey）
export function generateObjectKey(userUuid: string, projectId: string, fileId: string): string {
  return `${userUuid}/${projectId}/${fileId}`
}

// 检查云存储客户端是否已初始化
export function isOssClientReady(): boolean {
  return ossClient !== null && tokenExpirationTime > Date.now()
}

// 清除云存储客户端（用于重新初始化）
export function clearOssClient(): void {
  ossClient = null
  stsToken = null
  tokenExpirationTime = 0
  currentProvider = 'aliyun'
}
